import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { ChartCard } from '@/components/calculator/ChartCard';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ContributionTimingControls } from '@/components/calculator/ContributionTimingControls';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getFireFieldExplainers } from '@/content/field-explainers/fire';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateFire, type FireInput, type FireResult } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  formatPercent,
  parseContractState
} from './shared';

type FireFormState = {
  currentAge: number;
  retirementAge: number;
  growthRate: number;
  contributionIncrease: number;
  contributionFrequency: 'monthly' | 'yearly';
  initial401k: number;
  initialRoth: number;
  initialTaxable: number;
  contribution401k: number;
  contributionRoth: number;
  contributionTaxable: number;
  annualSpend: number;
};

const defaults: FireFormState = {
  currentAge: 30,
  retirementAge: 50,
  growthRate: 7,
  contributionIncrease: 0,
  contributionFrequency: 'monthly',
  initial401k: 50000,
  initialRoth: 25000,
  initialTaxable: 15000,
  contribution401k: 1000,
  contributionRoth: 500,
  contributionTaxable: 500,
  annualSpend: 80000
};

const chartConfig = {
  account401k: {
    label: '401(k)',
    color: 'var(--chart-1)'
  },
  accountRoth: {
    label: 'Roth IRA',
    color: 'var(--chart-2)'
  },
  accountTaxable: {
    label: 'Taxable',
    color: 'var(--chart-3)'
  },
  total: {
    label: 'Total',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

const balanceSeriesKeys = ['account401k', 'accountRoth', 'accountTaxable', 'total'] as const;

type Props = {
  route: RouteDefinition;
};

function toFiniteNumber(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

function toEngineInput(state: FireFormState): FireInput {
  const currentAge = Math.max(18, Math.round(toFiniteNumber(state.currentAge, defaults.currentAge)));
  const retirementAge = Math.max(currentAge + 1, Math.round(toFiniteNumber(state.retirementAge, defaults.retirementAge)));

  const growthRate = Math.max(0, toFiniteNumber(state.growthRate, defaults.growthRate));
  const contributionIncrease = Math.max(0, toFiniteNumber(state.contributionIncrease, defaults.contributionIncrease));

  const initial401k = Math.max(0, toFiniteNumber(state.initial401k, defaults.initial401k));
  const initialRoth = Math.max(0, toFiniteNumber(state.initialRoth, defaults.initialRoth));
  const initialTaxable = Math.max(0, toFiniteNumber(state.initialTaxable, defaults.initialTaxable));

  const contribution401k = Math.max(0, toFiniteNumber(state.contribution401k, defaults.contribution401k));
  const contributionRoth = Math.max(0, toFiniteNumber(state.contributionRoth, defaults.contributionRoth));
  const contributionTaxable = Math.max(0, toFiniteNumber(state.contributionTaxable, defaults.contributionTaxable));

  return {
    currentAge,
    retirementAge,
    growthRate,
    contributionIncrease,
    contributionFrequency: state.contributionFrequency,
    initialBalances: [initial401k, initialRoth, initialTaxable],
    contributions: [contribution401k, contributionRoth, contributionTaxable]
  };
}

function summarize(result: FireResult, annualSpend: number) {
  const finalYear = result.years[result.years.length - 1] ?? {
    year: 0,
    age: 0,
    balances: [0, 0, 0],
    total: 0
  };

  const normalizedAnnualSpend = Math.max(0, toFiniteNumber(annualSpend, defaults.annualSpend));
  const targetNumber = normalizedAnnualSpend * 25;
  const readinessRatio = targetNumber > 0 ? finalYear.total / targetNumber : 0;

  return {
    finalYear,
    targetNumber,
    readinessRatio,
    gap: Math.max(targetNumber - finalYear.total, 0)
  };
}

export function FireCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<FireFormState>(() => parseContractState('fire', defaults));
  const [visibleBalanceKeys, setVisibleBalanceKeys] = useState<string[]>(() => [...balanceSeriesKeys]);

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateFire(toEngineInput(state)), [state]);
  const summary = useMemo(() => summarize(result, state.annualSpend), [result, state.annualSpend]);

  const explainers = useMemo(() => {
    const entries = getFireFieldExplainers({
      input: {
        currentAge: state.currentAge,
        retirementAge: state.retirementAge,
        growthRate: state.growthRate,
        contributionIncrease: state.contributionIncrease,
        contributionFrequency: state.contributionFrequency,
        initial401k: state.initial401k,
        initialRoth: state.initialRoth,
        initialTaxable: state.initialTaxable,
        contribution401k: state.contribution401k,
        contributionRoth: state.contributionRoth,
        contributionTaxable: state.contributionTaxable,
        annualSpend: state.annualSpend
      },
      result: {
        normalizedContribution401k: result.normalizedContribution401k,
        finalAge: summary.finalYear.age,
        final401k: summary.finalYear.balances[0] ?? 0,
        finalRoth: summary.finalYear.balances[1] ?? 0,
        finalTaxable: summary.finalYear.balances[2] ?? 0,
        finalTotal: summary.finalYear.total,
        targetNumber: summary.targetNumber,
        readinessRatio: summary.readinessRatio,
        gap: summary.gap
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result, summary]);

  const chartData = useMemo(
    () =>
      result.years.map((year) => ({
        age: year.age,
        account401k: year.balances[0] ?? 0,
        accountRoth: year.balances[1] ?? 0,
        accountTaxable: year.balances[2] ?? 0,
        total: year.total
      })),
    [result.years]
  );

  const shareState = {
    currentAge: state.currentAge,
    retirementAge: state.retirementAge,
    growthRate: state.growthRate,
    contributionFrequency: state.contributionFrequency,
    initial401k: state.initial401k,
    initialRoth: state.initialRoth,
    initialTaxable: state.initialTaxable,
    contribution401k: state.contribution401k,
    contributionRoth: state.contributionRoth,
    contributionTaxable: state.contributionTaxable,
    contributionIncrease: state.contributionIncrease,
    annualSpend: state.annualSpend
  };

  return (
    <AppLayout showRouteHowItWorks={false}>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">FIRE Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Plan your path to financial independence with clear, account-level projections. Test a few assumptions,
          compare outcomes side by side, and use the year-by-year view to build confidence in your plan.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Portfolio and contribution assumptions"
          description="Adjust ages, return assumptions, and contributions to see how small changes can shift your timeline."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="currentAge" label="Current age" value={state.currentAge} min={18} max={85} parseMode="integer" explainer={explainers.byId.get('currentAge')} onChange={(value) => setState((prev) => ({ ...prev, currentAge: value }))} />
            <NumberField id="retirementAge" label="Retirement age" value={state.retirementAge} min={state.currentAge + 1} max={90} parseMode="integer" explainer={explainers.byId.get('retirementAge')} onChange={(value) => setState((prev) => ({ ...prev, retirementAge: value }))} />
            <NumberField id="growthRate" label="Growth rate" value={state.growthRate} min={0} max={15} step={0.1} suffix="%" explainer={explainers.byId.get('growthRate')} onChange={(value) => setState((prev) => ({ ...prev, growthRate: value }))} />
            <NumberField id="contributionIncrease" label="Annual contribution increase" value={state.contributionIncrease} min={0} max={10} step={0.1} suffix="%" explainer={explainers.byId.get('contributionIncrease')} onChange={(value) => setState((prev) => ({ ...prev, contributionIncrease: value }))} />
            <NumberField id="annualSpend" label="Target annual spend" value={state.annualSpend} min={12000} step={500} prefix="$" explainer={explainers.byId.get('annualSpend')} onChange={(value) => setState((prev) => ({ ...prev, annualSpend: value }))} />
          </div>

          <ContributionTimingControls
            value={state.contributionFrequency}
            onValueChange={(value) => setState((prev) => ({ ...prev, contributionFrequency: value }))}
            inflationAdjusted={state.contributionIncrease > 0}
            onInflationAdjustedChange={(enabled) =>
              setState((prev) => ({
                ...prev,
                contributionIncrease: enabled ? Math.max(prev.contributionIncrease, 1) : 0
              }))
            }
          />

          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="initial401k" label="Initial 401(k)" value={state.initial401k} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initial401k')} onChange={(value) => setState((prev) => ({ ...prev, initial401k: value }))} />
            <NumberField id="contribution401k" label="401(k) contribution" value={state.contribution401k} min={0} step={100} prefix="$" helperText="Automatically capped by engine contract." explainer={explainers.byId.get('contribution401k')} onChange={(value) => setState((prev) => ({ ...prev, contribution401k: value }))} />
            <NumberField id="initialRoth" label="Initial Roth IRA" value={state.initialRoth} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initialRoth')} onChange={(value) => setState((prev) => ({ ...prev, initialRoth: value }))} />
            <NumberField id="contributionRoth" label="Roth contribution" value={state.contributionRoth} min={0} step={100} prefix="$" explainer={explainers.byId.get('contributionRoth')} onChange={(value) => setState((prev) => ({ ...prev, contributionRoth: value }))} />
            <NumberField id="initialTaxable" label="Initial taxable" value={state.initialTaxable} min={0} step={1000} prefix="$" explainer={explainers.byId.get('initialTaxable')} onChange={(value) => setState((prev) => ({ ...prev, initialTaxable: value }))} />
            <NumberField id="contributionTaxable" label="Taxable contribution" value={state.contributionTaxable} min={0} step={100} prefix="$" explainer={explainers.byId.get('contributionTaxable')} onChange={(value) => setState((prev) => ({ ...prev, contributionTaxable: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Projected total" value={formatCurrency(summary.finalYear.total)} note={`at age ${summary.finalYear.age}`} trend={summary.readinessRatio >= 1 ? 'up' : 'flat'} explainer={explainers.byId.get('projectedTotal')} />
          <ResultSummaryCard label="FIRE target" value={formatCurrency(summary.targetNumber)} note="25x annual spend rule" trend="flat" explainer={explainers.byId.get('fireTarget')} />
          <ResultSummaryCard label="Funding ratio" value={formatPercent(summary.readinessRatio * 100)} note={summary.gap === 0 ? 'Target reached in projection horizon' : `${formatCurrency(summary.gap)} remaining`} trend={summary.readinessRatio >= 1 ? 'up' : 'down'} explainer={explainers.byId.get('fundingRatio')} />
          <ResultSummaryCard label="401(k) normalized contribution" value={formatCurrency(result.normalizedContribution401k)} note={`per ${state.contributionFrequency === 'monthly' ? 'month' : 'year'} after cap handling`} trend="flat" explainer={explainers.byId.get('normalizedContribution401k')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'fire', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({
                title: ok ? 'Share link copied' : 'Share link ready',
                description: ok ? shareUrl : `Copy manually: ${shareUrl}`,
                variant: ok ? 'success' : 'warning'
              });
            }}
            onExportCsv={() => {
              const headers = ['Age', '401k', 'Roth', 'Taxable', 'Total'];
              const rows = result.years.map((year) => [
                year.age,
                Math.round(year.balances[0] ?? 0),
                Math.round(year.balances[1] ?? 0),
                Math.round(year.balances[2] ?? 0),
                Math.round(year.total)
              ]);
              exportRowsToCsv(headers, rows, 'fire-projection.csv');
              toast({ title: 'CSV exported', description: 'fire-projection.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'FIRE Projection Results',
                subtitle: `Current age ${state.currentAge} to retirement age ${state.retirementAge}`,
                summaryLeft: [
                  `Growth rate: ${state.growthRate.toFixed(1)}%`,
                  `Contribution frequency: ${state.contributionFrequency}`,
                  `Annual spend target: ${formatCurrency(state.annualSpend)}`,
                  `Normalized 401(k) contribution: ${formatCurrency(result.normalizedContribution401k)}`
                ],
                summaryRight: [
                  `Final age: ${summary.finalYear.age}`,
                  `Projected total: ${formatCurrency(summary.finalYear.total)}`,
                  `FIRE target: ${formatCurrency(summary.targetNumber)}`,
                  `Funding ratio: ${formatPercent(summary.readinessRatio * 100)}`
                ],
                headers: ['Age', '401(k)', 'Roth', 'Taxable', 'Total'],
                rows: result.years.map((year) => [
                  year.age,
                  formatCurrency(year.balances[0] ?? 0),
                  formatCurrency(year.balances[1] ?? 0),
                  formatCurrency(year.balances[2] ?? 0),
                  formatCurrency(year.total)
                ]),
                filename: 'fire-projection.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'fire-projection.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Portfolio balances by age"
          description="Account-level balances and total portfolio across your full projection timeline."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                End age <span className="font-semibold text-foreground">{summary.finalYear.age}</span>
              </span>
              <span>
                Projected total <span className="font-semibold text-foreground">{formatCurrency(summary.finalYear.total)}</span>
              </span>
              <span>
                Target gap <span className="font-semibold text-foreground">{formatCurrency(summary.gap)}</span>
              </span>
            </div>
          }
          legend={
            <ChartLegendPills
              config={chartConfig}
              keys={balanceSeriesKeys}
              activeKeys={visibleBalanceKeys}
              onActiveKeysChange={setVisibleBalanceKeys}
            />
          }
        >
          <ChartContainer config={chartConfig} family="multi">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="age" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={70}
                tickFormatter={(value) => formatCompactCurrency(Number(value))}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              {visibleBalanceKeys.includes('account401k') ? <Line dataKey="account401k" type="natural" stroke="var(--color-account401k)" strokeWidth={2} dot={false} /> : null}
              {visibleBalanceKeys.includes('accountRoth') ? <Line dataKey="accountRoth" type="natural" stroke="var(--color-accountRoth)" strokeWidth={2} dot={false} /> : null}
              {visibleBalanceKeys.includes('accountTaxable') ? <Line dataKey="accountTaxable" type="natural" stroke="var(--color-accountTaxable)" strokeWidth={2} dot={false} /> : null}
              {visibleBalanceKeys.includes('total') ? (
                <Line dataKey="total" type="natural" stroke="var(--color-total)" strokeWidth={3.2} dot={false}>
                  <LabelList dataKey="total" position="top" offset={10} className="hidden fill-foreground text-[10px] xl:block" formatter={(value) => formatCompactCurrency(Number(value ?? 0))} />
                </Line>
              ) : null}
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Projection table" description="Complete yearly account balances and totals.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Age</TableHead>
                <TableHead numeric>401(k)</TableHead>
                <TableHead numeric>Roth</TableHead>
                <TableHead numeric>Taxable</TableHead>
                <TableHead numeric>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.years.map((year) => (
                <TableRow key={year.age}>
                  <TableCell numeric>{year.age}</TableCell>
                  <TableCell numeric>{formatCurrency(year.balances[0] ?? 0)}</TableCell>
                  <TableCell numeric>{formatCurrency(year.balances[1] ?? 0)}</TableCell>
                  <TableCell numeric>{formatCurrency(year.balances[2] ?? 0)}</TableCell>
                  <TableCell numeric>{formatCurrency(year.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, worked through with your own figures. Tap the info icon beside any field to read the same explanation without scrolling."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
